import React, { useCallback, useState } from 'react';
import { ExternalLink, MapPin } from 'lucide-react';
import { reportsAPI } from '../../../services/api';
import useReport from '../shared/useReport';
import KpiStrip from '../shared/KpiStrip';
import ReportTable from '../shared/ReportTable';
import PrintButton from '../shared/PrintButton';
import DateRangeFilter, { getPresetRange } from '../shared/DateRangeFilter';
import { formatClinicDate, formatClinicTime } from '../../../utils/clinicTime';
import { REVIEW_LABEL, formatDistance, roleLabel } from '../../attendance/attendanceStatus';
import { kpiMap } from './kpis';

// Two questions about where attendance was marked from: who never hands over a position at all, and
// whose punches come from somewhere other than where everybody else's come from.
//
// The second one is measured against the staff themselves, not against the hospital's recorded
// position. That is deliberate. It is the question the owner actually asked - "differs from the
// other staff" - and it keeps working while the hospital's own coordinates are unset, which they
// are until somebody fills them in under Settings. The centre is the middle punch in each
// direction, not the average, so a handful of far-off punches cannot drag it towards themselves and
// quietly excuse themselves.
//
// Nobody is accused on the strength of a bad fix: when a phone's own margin of error is wider than
// the distance being complained about, the punch is marked as proving nothing and left out of the
// count. A 2 km fix 900 m from the centre is a bad fix, not a person somewhere else.

const sideLabel = (side) => (side === 'IN' ? 'Arrived' : 'Left');
const time = (v) => formatClinicTime(v, { hour: 'numeric', minute: '2-digit', hour12: true }, '-');
const day = (v) => formatClinicDate(v, { weekday: 'short', day: 'numeric', month: 'short' });
const mapUrl = (lat, lng) => `https://www.google.com/maps?q=${lat},${lng}`;
const pct = (v) => (v == null ? '-' : `${Number(v).toFixed(1)}%`);

const Coords = ({ lat, lng }) =>
  lat == null || lng == null ? (
    '-'
  ) : (
    <a href={mapUrl(lat, lng)} target="_blank" rel="noreferrer" className="font-mono text-[11px] text-navy-700 hover:underline whitespace-nowrap">
      {Number(lat).toFixed(5)}, {Number(lng).toFixed(5)}
      <ExternalLink className="w-3 h-3 inline ml-1 -mt-0.5" aria-hidden="true" />
    </a>
  );

const NO_POSITION_COLUMNS = [
  {
    key: 'userName', label: 'Name', wrap: true,
    render: (r) => (
      <span className="flex items-center gap-2 flex-wrap">
        {r.userName}
        {r.percent >= 100 && (
          <span className="inline-flex rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-800">
            Never shares
          </span>
        )}
      </span>
    ),
    csv: (r) => r.userName,
    searchText: (r) => `${r.userName} ${r.percent >= 100 ? 'never shares' : ''}`,
  },
  { key: 'role', label: 'Role', render: (r) => roleLabel(r.role) },
  { key: 'punches', label: 'Punches', align: 'right' },
  {
    key: 'withoutPosition', label: 'With no position', align: 'right',
    render: (r) => <span className="font-semibold text-amber-700">{r.withoutPosition}</span>,
    csv: (r) => r.withoutPosition,
  },
  {
    key: 'percent', label: 'Share', align: 'right',
    render: (r) => <span className={`font-semibold ${r.percent >= 100 ? 'text-red-700' : 'text-amber-700'}`}>{pct(r.percent)}</span>,
    csv: (r) => r.percent,
  },
  {
    key: 'lastWithoutAt', label: 'Most recent', align: 'right', wrap: true,
    render: (r) => (r.lastWithoutAt ? `${day(r.lastWithoutAt)}, ${time(r.lastWithoutAt)}` : '-'),
    csv: (r) => r.lastWithoutAt || '',
  },
];

const OUTLIER_COLUMNS = [
  { key: 'workDate', label: 'Day', render: (r) => day(r.workDate), csv: (r) => r.workDate },
  { key: 'userName', label: 'Name', wrap: true },
  { key: 'role', label: 'Role', render: (r) => roleLabel(r.role) },
  { key: 'side', label: 'Punch', render: (r) => sideLabel(r.side), csv: (r) => sideLabel(r.side) },
  { key: 'at', label: 'Time', align: 'right', render: (r) => time(r.at), csv: (r) => r.at },
  {
    key: 'distanceMeters', label: 'Away from the others', align: 'right', wrap: true,
    render: (r) =>
      r.inconclusive ? (
        <span className="text-gray-500">{formatDistance(r.distanceMeters)}, but see the margin</span>
      ) : (
        <span className="font-semibold text-red-700">{formatDistance(r.distanceMeters)}</span>
      ),
    csv: (r) => r.distanceMeters,
    sortValue: (r) => Number(r.distanceMeters),
  },
  {
    key: 'accuracyMeters', label: 'Phone margin', align: 'right', wrap: true,
    render: (r) =>
      r.accuracyMeters == null
        ? 'Not given'
        : `± ${formatDistance(r.accuracyMeters)}${r.inconclusive ? ', proves nothing' : ''}`,
    csv: (r) => r.accuracyMeters,
    sortValue: (r) => (r.accuracyMeters == null ? -1 : Number(r.accuracyMeters)),
  },
  { key: 'latitude', label: 'Position', render: (r) => <Coords lat={r.latitude} lng={r.longitude} />, csv: (r) => `${r.latitude}, ${r.longitude}` },
  {
    key: 'reviewStatus', label: 'Decision',
    // REVIEW_LABEL calls NONE "Not needed", which is right on the Flagged Punches screen: there it
    // means the punch passed the hospital's own position check. Here it would read as a verdict of
    // innocence beside a row saying somebody was 12 km from everybody else, so say what is true -
    // nobody has looked at it.
    render: (r) => (!r.reviewStatus || r.reviewStatus === 'NONE' ? 'Not reviewed' : REVIEW_LABEL[r.reviewStatus] || r.reviewStatus),
    csv: (r) => r.reviewStatus || '',
  },
];

/** How the centre was arrived at, so the distances below are a reading and not a verdict. */
const CentreNote = ({ data, loading }) => {
  if (loading) return <div className="bg-white rounded-2xl shadow p-4 h-24 animate-pulse" />;
  const centre = data?.centre;
  if (!centre) {
    return (
      <div className="bg-white rounded-2xl shadow p-4 text-sm text-gray-600">
        <p className="font-semibold text-gray-900 mb-1">Not enough positions to compare yet</p>
        <p>
          Fewer than five punches in this range carried a position, so there is nothing to measure one against.
          Nobody is singled out below until there is a crowd to compare them with.
        </p>
      </div>
    );
  }
  return (
    <div className="bg-white rounded-2xl shadow p-4 text-sm text-gray-600 space-y-1.5">
      <p className="font-semibold text-gray-900 flex items-center gap-1.5">
        <MapPin className="w-4 h-4 text-navy-700" aria-hidden="true" />
        Where staff normally punch
      </p>
      <p>
        Taken from the {centre.fromPunches} punches in this range that carried a position, using the middle one in each
        direction so that a few far-off punches cannot move it: <Coords lat={centre.latitude} lng={centre.longitude} />
      </p>
      <p>
        Most punches land within {formatDistance(data.typicalDistanceMeters)} of that spot, so a punch is listed below once
        it is more than {formatDistance(data.thresholdMeters)} away.
      </p>
      <p className="text-gray-500">
        This is where staff actually punch, which need not be the hospital's recorded position. It answers a different
        question from the Flagged Punches report, which measures against the hospital's own coordinates.
      </p>
    </div>
  );
};

const PunchLocationsReport = () => {
  const [range, setRange] = useState(() => getPresetRange('thisMonth'));
  const [preset, setPreset] = useState('thisMonth');

  const fetcher = useCallback(
    () => reportsAPI.getAttendancePositions({ from: range.from, to: range.to }),
    [range],
  );
  const { data, loading } = useReport(fetcher);

  const noPosition = data?.noPosition || [];
  const outliers = data?.outliers || [];
  const k = kpiMap(data);
  const never = noPosition.filter((r) => r.percent >= 100).length;
  const judged = outliers.filter((r) => !r.inconclusive);

  const kpis = [
    { label: 'No position', value: k.withoutPosition ?? noPosition.reduce((s, r) => s + r.withoutPosition, 0), sub: 'Punches with none at all' },
    { label: 'Never shared one', value: k.neverShared ?? never, sub: 'Every punch of theirs', tone: never > 0 ? 'alert' : undefined },
    { label: 'Away from the rest', value: k.outliers ?? judged.length, sub: `Over ${data?.thresholdMeters ? formatDistance(data.thresholdMeters) : 'the usual spread'} from the centre`, tone: judged.length > 0 ? 'alert' : undefined },
    { label: 'People involved', value: k.outlierPeople ?? new Set(judged.map((r) => r.userId)).size, sub: 'Staff with such a punch' },
    { label: 'Proves nothing', value: outliers.length - judged.length, sub: 'Margin wider than distance' },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[300px]">
          <DateRangeFilter value={range} preset={preset} onChange={(r, p) => { setRange(r); setPreset(p); }} />
        </div>
        <div className="pt-4"><PrintButton /></div>
      </div>

      <KpiStrip items={kpis} loading={loading} />

      <CentreNote data={data} loading={loading} />

      <ReportTable
        title="Staff who did not share their location"
        columns={NO_POSITION_COLUMNS}
        rows={noPosition}
        csvName={`punch-no-position-${range.from}-to-${range.to}`}
        emptyText="Every punch in the selected range carried a position"
        footnote="A punch carries no position when the phone was refused permission, or could not get a fix indoors. Somebody at 100% has never shared one, which is worth asking about; an occasional miss is normal."
      />

      <ReportTable
        title="Punches away from where everybody else punches"
        columns={OUTLIER_COLUMNS}
        rows={outliers}
        csvName={`punch-outliers-${range.from}-to-${range.to}`}
        emptyText="Every positioned punch in the selected range came from where staff normally punch"
        footnote="Distances are from the middle of where staff punch, not from the hospital's recorded position. A row marked as proving nothing had a phone margin of error wider than the distance itself, so it is shown for completeness and left out of the count. Decide a punch on the attendance board, where the selfie is."
      />
    </div>
  );
};

export default PunchLocationsReport;
